// The testimonial form on /testimonials/new (pages/testimonials-new.html).
// All the copy, the error messages and the thank-you card included, is in
// the page, so it can be translated: this runs the star rating, the
// character count, the Cloudflare Turnstile widget and the submission, a
// POST to the cloud app (POST /api/testimonials), which checks the Turnstile
// token and stores the testimonial for review in its admin dashboard. Once
// it is in, the thank-you card takes the form's place.
import { Controller } from "stimulus"
import Email from "../lib/email"
import Honeybadger from "../honeybadger"

const TURNSTILE_SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
// Must match Turnstile::TESTIMONIAL_ACTION in the cloud app.
const TURNSTILE_ACTION = 'testimonial'

let turnstileLoading = null

// Loads Cloudflare's script once (it stays in <head> across Turbo visits)
// and resolves when window.turnstile is ready to render widgets.
function loadTurnstile() {
  if (window.turnstile) return Promise.resolve()
  if (turnstileLoading) return turnstileLoading
  turnstileLoading = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = TURNSTILE_SCRIPT
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => {
      turnstileLoading = null
      reject(new Error('The Turnstile script failed to load'))
    }
    document.head.appendChild(script)
  })
  return turnstileLoading
}

export default class TestimonialFormController extends Controller {

  static get targets() {
    return [
      'form', 'thanks', 'rating', 'quote', 'count', 'name', 'email', 'title', 'website',
      'turnstile', 'error', 'serverMessage', 'submit'
    ]
  }

  urlBase = window.jekyllEnv === 'production' ? 'https://app.beekeeperstudio.io' : 'http://localhost:3000'
  widgetId = null
  token = null

  connect() {
    // A Turbo snapshot can hold the thank-you state, a stale count or a
    // rendered widget whose token is long gone.
    this.formTarget.hidden = false
    this.thanksTarget.hidden = true
    this.hideError()
    this.setRating(parseInt(this.ratingTarget.value, 10) || 5)
    this.count()
    this.turnstileTarget.innerHTML = ''
    this.renderTurnstile()
  }

  disconnect() {
    if (this.widgetId !== null && window.turnstile) {
      try { window.turnstile.remove(this.widgetId) } catch (error) { /* already gone */ }
    }
    this.widgetId = null
    this.token = null
  }

  get siteKey() {
    return this.data.get('site-key') || ''
  }

  // The widget, when the site has a key (site.cloudflare.turnstile_site_key).
  // Without one the form posts without a token, which the cloud app accepts
  // only while Turnstile is not configured there either (development).
  renderTurnstile() {
    if (!this.siteKey) return
    loadTurnstile().then(() => {
      if (!this.element.isConnected || this.widgetId !== null) return
      this.widgetId = window.turnstile.render(this.turnstileTarget, {
        sitekey: this.siteKey,
        action: TURNSTILE_ACTION,
        theme: 'dark',
        callback: (token) => { this.token = token },
        'expired-callback': () => { this.token = null },
        'error-callback': () => { this.token = null }
      })
    }).catch((error) => {
      Honeybadger.notify(error)
    })
  }

  // Tokens are single-use: after a submission the cloud app refused, the
  // visitor needs a fresh one.
  resetTurnstile() {
    this.token = null
    if (this.widgetId !== null && window.turnstile) window.turnstile.reset(this.widgetId)
  }

  rate(event) {
    this.setRating(parseInt(event.currentTarget.dataset.rating, 10))
  }

  setRating(rating) {
    this.ratingTarget.value = rating
    this.element.querySelectorAll('.tmf-star').forEach((star) => {
      const value = parseInt(star.dataset.rating, 10)
      star.classList.toggle('is-on', value <= rating)
      star.setAttribute('aria-checked', value === rating ? 'true' : 'false')
    })
    this.element.querySelectorAll('[data-rating-label]').forEach((label) => {
      label.hidden = parseInt(label.dataset.ratingLabel, 10) !== rating
    })
  }

  count() {
    this.countTarget.textContent = this.quoteTarget.value.length
  }

  payload() {
    return {
      name: this.nameTarget.value.trim(),
      email: this.emailTarget.value.trim(),
      title: this.titleTarget.value.trim(),
      quote: this.quoteTarget.value.trim(),
      rating: parseInt(this.ratingTarget.value, 10) || null
    }
  }

  // Which answer needs fixing (the data-error of its message), or null.
  validate(payload) {
    if (!payload.quote) return 'quote'
    if (!payload.name) return 'name'
    if (!Email.validate(payload.email)) return 'email'
    if (this.siteKey && !this.token) return 'turnstile'
    return null
  }

  submit(event) {
    event.preventDefault()
    this.hideError()
    const payload = this.payload()
    const problem = this.validate(payload)
    if (problem) {
      this.showError(problem)
      return
    }

    this.submitTarget.disabled = true
    const body = {
      testimonial: payload,
      website: this.websiteTarget.value,
      'cf-turnstile-response': this.token
    }
    fetch(`${this.urlBase}/api/testimonials`, {
      method: 'POST',
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).then(async (response) => {
      if (response.ok) {
        this.showThanks()
        this.track('testimonial-submitted')
        return
      }
      let message = null
      try {
        const json = await response.json()
        message = json.friendly_error || json.error
      } catch (error) { /* no JSON body */ }
      this.resetTurnstile()
      if (response.status === 422) {
        this.showError('turnstile')
      } else {
        this.showError(message ? 'message' : 'server', message)
      }
      if (response.status >= 500) Honeybadger.notify(new Error(`testimonial request failed with status ${response.status}`))
    }).catch((error) => {
      Honeybadger.notify(error)
      this.resetTurnstile()
      this.showError('server')
    }).finally(() => {
      this.submitTarget.disabled = false
    })
  }

  showThanks() {
    this.formTarget.hidden = true
    this.thanksTarget.hidden = false
    this.thanksTarget.scrollIntoView({ block: 'start', behavior: 'smooth' })
    this.thanksTarget.focus({ preventScroll: true })
  }

  showError(problem, message) {
    this.errorTarget.querySelectorAll('[data-error]').forEach((el) => {
      el.hidden = el.dataset.error !== problem
    })
    if (problem === 'message') this.serverMessageTarget.textContent = message
    this.errorTarget.hidden = false
  }

  hideError() {
    this.errorTarget.querySelectorAll('[data-error]').forEach((el) => { el.hidden = true })
    this.errorTarget.hidden = true
  }

  track(eventName) {
    try {
      if (window.isDevelopment) {
        console.log('DEV TRACK:', eventName)
      } else if (window.fathom) {
        window.fathom.trackEvent(eventName)
      }
    } catch (error) {
      Honeybadger.notify(error)
    }
  }
}
